import { IconButton, InputAdornment, TextField } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { ChangeEvent, useState } from "react";

import type { ConnectionFieldName, ConnectionFieldRules } from "features/adminUsers/adminUsers";

import {
  ConnectionFieldMessages,
  ConnectionValues,
  hostDomainWarning,
  shownConnectionFields,
} from "./connectionFields";

export type ConnectionFieldsProps = {
  rules: ConnectionFieldRules;
  vendor: string;
  values: ConnectionValues;
  onChange: (name: ConnectionFieldName, value: string) => void;
  /** Refusals at the field: the form's own check, then the server's. */
  errors?: ConnectionFieldMessages;
  disabled?: boolean;
};

/** Host, client code, username and password of a bot login, in that order,
 *  with the vendor's labels, required marks and hints (CAL-6158). The one
 *  copy every connection form renders; a field the vendor never reads is not
 *  shown at all. */
export default function ConnectionFields({
  rules,
  vendor,
  values,
  onChange,
  errors = {},
  disabled = false,
}: ConnectionFieldsProps) {
  const [showPassword, setShowPassword] = useState(false);
  const hostWarning = hostDomainWarning(rules, vendor, values.host);

  return (
    <>
      {shownConnectionFields(rules).map((name) => {
        const rule = rules[name];
        const error = errors[name];
        const warning = name === "host" && !error ? hostWarning : null;
        const isPassword = name === "password";
        return (
          <TextField
            key={name}
            fullWidth
            sx={{ mb: 1.5 }}
            name={name}
            label={rule.label}
            required={rule.use === "required"}
            disabled={disabled}
            value={values[name]}
            placeholder={rule.example || undefined}
            onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(name, e.target.value)}
            error={Boolean(error)}
            helperText={error || warning || rule.hint || undefined}
            FormHelperTextProps={
              warning ? { sx: { color: "warning.dark" } } : undefined
            }
            type={isPassword && !showPassword ? "password" : "text"}
            inputProps={{
              maxLength: rule.max_length ?? undefined,
              autoComplete: isPassword ? "new-password" : "off",
              "data-field": name,
            }}
            InputProps={
              isPassword
                ? {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={() => setShowPassword((v) => !v)}
                          aria-label="toggle password visibility"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
                : undefined
            }
          />
        );
      })}
    </>
  );
}
